<template>
    <div class="entrada">
        <div class="entrada__cartao">
            <h1 class="entrada__titulo">hLiberty · Atendimento</h1>

            <template v-if="!local">
                <p class="entrada__texto">
                    {{ $t('components.atendimento.entrada.somenteAdmin') }}
                </p>
                <v-btn color="primary" variant="flat" block @click="irParaOAdmin">
                    {{ $t('common.labels.backToAdmin') }}
                </v-btn>
            </template>

            <template v-else>
                <p class="entrada__texto">
                    {{ $t('components.atendimento.entrada.explicacao') }}
                </p>

                <v-alert type="warning" variant="tonal" density="compact" class="mb-4">
                    {{ $t('components.atendimento.entrada.aviso') }}
                </v-alert>

                <v-progress-linear v-if="carregando" indeterminate color="primary" class="mb-3" />

                <v-alert v-if="erro" type="error" variant="tonal" density="compact" class="mb-3">
                    {{ erro }}
                </v-alert>

                <v-list density="compact" class="entrada__lista">
                    <v-list-item
                        v-for="agente in agentes"
                        :key="agente.login"
                        :title="agente.name"
                        :subtitle="`${papel(agente.role)} · ${agente.login}`"
                        @click="entrar(agente.login)">
                        <template #prepend>
                            <v-avatar size="32" color="primary">
                                <span class="text-caption">{{ iniciais(agente.name) }}</span>
                            </v-avatar>
                        </template>
                    </v-list-item>
                </v-list>
            </template>
        </div>
    </div>
</template>

<script setup>
/**
 * Entrada local de desenvolvimento.
 *
 * Em ambiente publicado esta tela nao cria sessao nenhuma: ela so explica que
 * a entrada e pelo admin e oferece o caminho de volta. A lista de agentes so
 * aparece quando **os dois lados** estao em modo local -- o /config desta
 * aplicacao e o ATENDIMENTO_LOGIN_LOCAL da API. Sem isso o endpoint responde
 * 404 e a lista fica vazia.
 *
 * O token que chega aqui e emitido pela API de atendimento com o mesmo formato
 * do liberty/auth (ver DevTokenIssuer): assim o caminho exercitado na maquina
 * e o mesmo que roda no cluster, menos a origem do token.
 */
import { onMounted, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { setAuth } from '@/services/api/auth.js';
import { agentesLocais, entrarLocal } from '@/services/api/atendimento.js';
import { voltarParaOAdmin, DESTINO_PADRAO } from '@/services/sso.js';

const router = useRouter();
const route = useRoute();

const local = ref(!!window.LOGIN_LOCAL);
const agentes = ref([]);
const carregando = ref(false);
const erro = ref('');

const papeis = { agent: 'Atendente', supervisor: 'Supervisor', admin: 'Administrador' };
const papel = (chave) => papeis[chave] ?? chave;

function iniciais(nome) {
    return String(nome || '?')
        .split(/\s+/)
        .slice(0, 2)
        .map((parte) => parte[0])
        .join('')
        .toUpperCase();
}

function irParaOAdmin() {
    voltarParaOAdmin({ motivo: 'sem-sessao' });
}

async function entrar(login) {
    carregando.value = true;
    erro.value = '';

    try {
        const resposta = await entrarLocal(login);
        const agente = resposta.agent?.data ?? resposta.agent;

        setAuth({
            token: resposta.token,
            // O /me do liberty/auth nao existe no modo local: monta-se aqui o
            // minimo que `getUser()` le, a partir do que a API devolveu.
            credentials: {
                user: {
                    id: agente?.platform_user_id,
                    login: agente?.login,
                    name: agente?.name,
                    email: agente?.email,
                    corps: {},
                },
                corp: 'demo',
            },
        });

        localStorage.setItem('corp', 'demo');

        await router.replace(route.query.de || DESTINO_PADRAO);
    } catch (e) {
        erro.value = e?.response?.data?.message || e.message || 'Nao foi possivel entrar.';
    } finally {
        carregando.value = false;
    }
}

onMounted(async () => {
    if (!local.value) return;

    carregando.value = true;

    try {
        const resposta = await agentesLocais();
        agentes.value = resposta.agents ?? [];
    } catch (e) {
        erro.value = 'A API de atendimento nao esta com o login local ligado (ATENDIMENTO_LOGIN_LOCAL).';
    } finally {
        carregando.value = false;
    }
});
</script>

<style scoped lang="scss">
.entrada {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f4f5f8;
    padding: 24px;

    &__cartao {
        background: #fff;
        border-radius: 14px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, .08);
        padding: 28px;
        width: 100%;
        max-width: 420px;
    }

    &__titulo {
        font-size: 18px;
        margin: 0 0 6px;
    }

    &__texto {
        color: var(--secondaryColor);
        font-size: 14px;
        margin: 0 0 16px;
    }

    &__lista {
        border: 1px solid #e6e8ee;
        border-radius: 10px;
        padding: 0;
    }
}
</style>
